<template>
	<div class="lcsj-filter" :class="{ 'is-grid': columns > 0, 'is-expanded': expanded, 'is-stacked': stackedActions, 'is-tail-inline': tailInline && stackedActions && expanded }">
		<el-form :inline="true" class="lcsj-filter__form" @submit.native.prevent>
			<div class="lcsj-filter__row">
				<div class="lcsj-filter__actions" role="toolbar" aria-label="列表操作"><div ref="actions" class="lcsj-filter__buttons">
					<button v-if="hasMore" ref="toggle" type="button" class="lcsj-filter__toggle" :aria-expanded="String(expanded)" :aria-controls="fieldsId + ' ' + moreId" :title="expanded ? '收起筛选' : moreText" @click="toggle">
						{{ expanded ? '收起筛选' : '更多筛选' }}<i :class="expanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
					</button>
					<el-button v-if="showSearch" type="primary" size="small" icon="el-icon-search" @click="$emit('search')">搜索</el-button>
					<el-button v-if="$listeners.reset" size="small" icon="el-icon-refresh-left" @click="$emit('reset')">重置</el-button>
					<slot name="query"></slot><slot name="actions"></slot></div>
				</div>
				<div :id="fieldsId" class="lcsj-filter__viewport" :style="expanded ? null : { maxHeight: firstRowHeight + 'px' }">
					<div ref="fields" class="lcsj-filter__fields" :style="columns > 0 ? { gridTemplateColumns: gridLayout } : null"><slot></slot><slot name="common"></slot><div :id="moreId" v-show="hasMoreSlot" class="lcsj-filter__more"><slot name="more"></slot></div></div>
				</div>
			</div>
		</el-form>
	</div>
</template>
<script>
// 筛选项始终保留在原位置，折叠不会重建控件或丢失输入值。
export default {
	name: 'lcsj-filter',
	props: {
		columns: { type: Number, default: 6 },
		moreText: { type: String, default: '更多筛选' },
		defaultExpanded: { type: Boolean, default: false },
		// 旧页面已提供带权限的搜索按钮时关闭内置按钮。
		showSearch: { type: Boolean, default: true }
	},
	data() { return { expanded: this.defaultExpanded, overflow: false, firstRowHeight: 40, stackedActions: false, tailInline: false, gridLayout: 'repeat(1, minmax(0, 180px))' }; },
	computed: {
		fieldsId() { return 'lcsj-filter-fields-' + this._uid; },
		moreId() { return 'lcsj-filter-more-' + this._uid; },
		hasMoreSlot() { return !!(this.$slots.more || this.$scopedSlots.more); },
		hasMore() { return this.overflow; }
	},
	mounted() {
		if (typeof ResizeObserver !== 'undefined') {
			this.observer = new ResizeObserver(this.scheduleMeasure);
			this.observer.observe(this.$refs.fields);
			this.observer.observe(this.$refs.actions);
			this.observer.observe(this.$el);
		}
		window.addEventListener('resize', this.scheduleMeasure);
		this.scheduleMeasure();
	},
	activated() { this.scheduleMeasure(); },
	updated() { this.scheduleMeasure(); },
	beforeDestroy() {
		if (this.observer) this.observer.disconnect();
		window.removeEventListener('resize', this.scheduleMeasure);
		cancelAnimationFrame(this.measureFrame);
	},
	methods: {
		scheduleMeasure() {
			cancelAnimationFrame(this.measureFrame);
			this.measureFrame = requestAnimationFrame(this.measure);
		},
		measure() {
			const fields = this.$refs.fields;
			if (!fields || !this.$el.clientWidth) return;
			const items = Array.from(fields.children).reduce((all, el) => all.concat(el.classList.contains('lcsj-filter__more') ? Array.from(el.children) : [el]), []).filter(el => el.offsetHeight > 0);
			const gap = parseFloat(getComputedStyle(fields).columnGap) || 0;
			const required = items.reduce((width, el) => width + el.getBoundingClientRect().width, 0) + Math.max(0, items.length - 1) * gap;
			const actions = this.$refs.actions;
			const buttons = Array.from(actions.children).filter(el => getComputedStyle(el).display !== 'none' && el !== this.$refs.toggle);
			const buttonWidth = buttons.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0) + Math.max(0, buttons.length - 1) * 8;
			const rootStyle = getComputedStyle(this.$el);
			const contentWidth = this.$el.clientWidth - parseFloat(rootStyle.paddingLeft) - parseFloat(rootStyle.paddingRight);
            // Measure against the full container, not the already reduced field viewport.
            // Otherwise switching between one/two rows changes the measured widths and oscillates.
            // Match the order page: six desktop fields with labels on the left.
            // Narrow dialogs and side panels also need the compact layout.
            const viewportWidth = typeof window === 'undefined' ? contentWidth : window.innerWidth;
            const responsiveColumns = viewportWidth <= 600 || contentWidth < 480 ? 1
                : viewportWidth <= 1100 || contentWidth < 900 ? 3 : 6;
            const fullColumns = Math.max(1, Math.min(this.columns, responsiveColumns));
            const cellWidth = Math.max(0, (contentWidth - (fullColumns - 1) * gap) / fullColumns);
            const fieldsWidth = this.columns > 0
                ? items.length * cellWidth + Math.max(0, items.length - 1) * gap
                : required;
            const stacked = items.length > 0 && fieldsWidth + buttonWidth + (buttons.length ? 16 : 0) > contentWidth + 1;
            const layout = 'repeat(' + Math.max(1, Math.min(items.length, fullColumns)) + ', minmax(0, ' + cellWidth + 'px))';
            if (this.stackedActions !== stacked || (this.columns > 0 && this.gridLayout !== layout)) {
                this.tailInline = false;
                this.stackedActions = stacked;
                this.gridLayout = layout;
                this.scheduleMeasure();
                return; // Re-read actual row positions after Vue has applied the new layout.
            }
            this.overflow = items.some(el => el.offsetTop > items[0].offsetTop + 1);
            // Keep the full-width field grid. Only occupy unused space on its last row.
            // Moving actions into the field viewport would shrink every earlier row.
            const lastTop = items.length ? Math.max(...items.map(el => el.offsetTop)) : 0;
            const lastRow = items.filter(el => Math.abs(el.offsetTop - lastTop) < 1);
            const fieldsLeft = fields.getBoundingClientRect().left;
            const lastRight = lastRow.length ? Math.max(...lastRow.map(el => el.getBoundingClientRect().right - fieldsLeft)) : 0;
            const toggleWidth = this.overflow ? (this.$refs.toggle ? this.$refs.toggle.getBoundingClientRect().width : 88) + (buttons.length ? 8 : 0) : 0;
            const toolbarWidth = buttonWidth + toggleWidth + 6; // Keep focus-ring padding clear of fields.
            this.tailInline = stacked && this.expanded && items.length > 0 && toolbarWidth + 16 <= contentWidth - lastRight;
			const firstTop = items.length ? items[0].offsetTop : 0;
			const firstBottom = firstTop + (items.length ? items[0].offsetHeight : 32);
			let height = 32;
			items.forEach(el => {
				const hidden = !this.expanded && el.offsetTop >= firstBottom;
				el.classList.toggle('lcsj-filter__hidden-field', hidden);
				if (hidden) {
					el.setAttribute('aria-hidden', 'true');
					el.setAttribute('inert', '');
				} else {
					el.removeAttribute('aria-hidden');
					el.removeAttribute('inert');
				}
				if (el.offsetTop < firstBottom) height = Math.max(height, el.offsetTop + el.offsetHeight);
			});
			this.firstRowHeight = height;
		},
		toggle() { this.expanded = !this.expanded; this.$emit('toggle', this.expanded); }
	}
};
</script>
<style lang="scss">
.lcsj-filter {
	background: var(--app-surface); border: 1px solid var(--app-border); border-radius: var(--app-radius-md);
	padding: 14px 16px; margin-bottom: 12px;
	.lcsj-filter__form { margin: 0 !important; }
	.lcsj-filter__row { display: flex; flex-direction: row-reverse; align-items: flex-end; gap: 16px; }
	.lcsj-filter__viewport { flex: 1; min-width: 0; overflow: hidden; }
	.lcsj-filter__fields, .lcsj-filter__more { position: relative; display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
	.lcsj-filter__more { display: contents; }
	.lcsj-filter__actions { position: relative; z-index: 1; flex: 0 1 auto; min-width: 0; max-width: 100%; overflow-x: auto; padding: 3px; margin: -3px; }
	.lcsj-filter__buttons { display: flex; width: max-content; min-width: 100%; align-items: center; justify-content: flex-end; flex-wrap: nowrap; gap: 8px; }
	.lcsj-filter__buttons > * { flex: 0 0 auto; white-space: nowrap; }
	.lcsj-filter__buttons .el-button + .el-button { margin-left: 0; }
	&.is-stacked {
		.lcsj-filter__row { flex-direction: column-reverse; align-items: stretch; gap: 12px; }
		.lcsj-filter__viewport { flex: none; width: 100%; }
		.lcsj-filter__actions { width: 100%; box-sizing: border-box; align-self: flex-end; margin: 0; }
        .lcsj-filter__buttons { width: 100%; min-width: 0; flex-wrap: wrap; }
	}
    &.is-tail-inline {
        .lcsj-filter__row { position: relative; }
        .lcsj-filter__actions { position: absolute; right: 0; bottom: 0; width: auto; margin: -3px; }
        .lcsj-filter__buttons { width: max-content; min-width: 0; flex-wrap: nowrap; }
    }
	.lcsj-filter__toggle {
		display: inline-flex; align-items: center; gap: 5px; height: 32px; padding: 0 4px;
		border: 0; border-radius: var(--app-radius-sm); background: transparent;
		color: var(--app-primary-text); font: inherit; font-size: 12px; white-space: nowrap; cursor: pointer;
		&:hover { background: var(--app-primary-soft); }
		&:focus-visible { outline: 2px solid var(--app-primary); outline-offset: 2px; }
	}
	.el-form-item { display: inline-flex; align-items: center; flex: 0 0 auto; max-width: 100%; margin: 0 !important; }
	.el-form-item__label { flex-shrink: 0; padding-right: 8px; line-height: 32px; color: var(--app-text-secondary); font-size: 12px; }
	.el-form-item__content { min-width: 0; line-height: 32px; }
	.el-form-item__content > .el-input:only-child, .el-form-item__content > .el-select:only-child { width: 180px !important; max-width: 100%; }
	.el-form-item__content > .el-date-editor--daterange, .el-form-item__content > .el-date-editor--datetimerange { max-width: 100%; }
	// 查询控件统一尺寸，覆盖页面遗留的内联宽度和 mini / medium 尺寸。
	.lcsj-filter__fields {
		.el-input, .el-select, .el-cascader, .el-input-number { width: 180px !important; max-width: 100%; }
		.el-input__inner { height: 32px !important; line-height: 32px !important; }
		.el-input__icon, .el-input-number__increase, .el-input-number__decrease { line-height: 30px !important; }
		.el-select .el-input, .el-cascader .el-input, .el-input-number .el-input { width: 100% !important; }
		.el-date-editor--daterange, .el-date-editor--datetimerange { width: 320px !important; height: 32px; }
		.el-range-input, .el-range-separator, .el-range__icon, .el-range__close-icon { line-height: 24px !important; }
	}
	// 标签与控件同行；桌面每行六项，少量筛选项为操作按钮保留空间。
	&.is-grid {
		.lcsj-filter__fields { display: grid; align-items: start; }
		.el-form-item { display: flex; flex-direction: row; align-items: center; min-width: 0; width: 100%; }
		.el-form-item__label { flex: 0 0 auto; width: auto !important; padding-right: 8px; text-align: left; line-height: 32px; white-space: nowrap; }
		.el-form-item__content { flex: 1 1 0; width: 0; min-width: 0; margin-left: 0 !important; }
		.el-form-item__content .el-input,
		.el-form-item__content .el-select,
		.el-form-item__content .el-cascader,
		.el-form-item__content .el-input-number,
		.el-form-item__content .el-date-editor { width: 100% !important; min-width: 0; max-width: 100%; }
		.el-range-editor { padding-left: 4px; padding-right: 4px; }
		.el-range-input { min-width: 0; font-size: 12px; }
		.el-range-separator { padding: 0; min-width: 12px; }
	}
	.lcsj-filter__hidden-field { pointer-events: none; }
	.lcsj-filter__hidden-field, .lcsj-filter__hidden-field * { visibility: hidden !important; }
}
.lcsj-chip {
	font-family: inherit; font-size: var(--app-font-base); padding: 4px 11px; border-radius: 15px;
	border: 1px solid var(--app-border-strong); background: var(--app-surface); color: var(--app-text-secondary);
	cursor: pointer; white-space: nowrap; transition: all .2s;
	&:hover { color: var(--app-primary-text); border-color: var(--app-primary-border); }
	&.is-on { background: var(--app-primary); border-color: var(--app-primary); color: var(--app-primary-contrast); font-weight: 500; }
}
</style>
